import { doc, getDoc, setDoc, updateDoc, serverTimestamp, increment, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getEffectiveTenantId } from './tenantContext';
import { logAuditEvent } from './auditService';
import { createReportBuildContext, buildClientReport } from './clientReportBuilders';
import { serializeReport, reviveReport } from '../utils/reportSerialization';
import { buildPeriod } from '../utils/reportFormat';

/**
 * Frozen client-wide report snapshots, one versioned set per month:
 *
 *   tenants/{tid}/reportSnapshots/{ym}                        pointer: currentVersion, frozenAt/By
 *   tenants/{tid}/reportSnapshots/{ym}/versions/{vid}         manifest: status, clients, parts
 *   tenants/{tid}/reportSnapshots/{ym}/versions/{vid}/parts/{reportId}__{clientKey}__{i}
 *
 * Payloads are JSON chunked across part docs to stay under Firestore's 1 MiB
 * document limit. Versions are append-only: re-freezing writes a new version and
 * moves the pointer; earlier versions are never modified or deleted.
 */

const COL = 'reportSnapshots';
export const SNAPSHOT_SCHEMA_VERSION = 1;
export const ALL_CLIENTS_KEY = '__all__';
export const UNASSIGNED_CLIENT_KEY = '__unassigned__';
/** Characters per part doc; JSON is mostly ASCII, leaving ample headroom under 1 MiB. */
const CHUNK_CHARS = 300_000;

function snapshotRef(tenantId, ym) {
  return doc(db, 'tenants', tenantId, COL, ym);
}

function versionRef(tenantId, ym, versionId) {
  return doc(db, 'tenants', tenantId, COL, ym, 'versions', versionId);
}

function partRef(tenantId, ym, versionId, partId) {
  return doc(db, 'tenants', tenantId, COL, ym, 'versions', versionId, 'parts', partId);
}

export function snapshotPartKey(reportId, clientKey) {
  return `${reportId}__${clientKey}`;
}

/** Current pointer + ready manifest for a month, or null when none is frozen. */
export async function getCurrentReportSnapshot(ym, tenantIdArg = null) {
  if (!ym) return null;
  const tenantId = tenantIdArg || (await getEffectiveTenantId());
  if (!tenantId) return null;
  const pointerSnap = await getDoc(snapshotRef(tenantId, ym));
  if (!pointerSnap.exists()) return null;
  const pointer = { id: pointerSnap.id, ...pointerSnap.data() };
  if (!pointer.currentVersion) return null;
  const manifestSnap = await getDoc(versionRef(tenantId, ym, pointer.currentVersion));
  if (!manifestSnap.exists()) return null;
  const manifest = { id: manifestSnap.id, ...manifestSnap.data() };
  if (manifest.status !== 'ready') return null;
  return { tenantId, ym, pointer, manifest };
}

/** All versions for a month (newest first) for the settings page history. */
export async function listReportSnapshotVersions(ym) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !ym) return [];
  const snap = await getDocs(collection(db, 'tenants', tenantId, COL, ym, 'versions'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.id).localeCompare(String(a.id)));
}

const payloadCache = new Map();

/**
 * Load one frozen report payload ({ type, report, patientCount, periodLabelOverride? }).
 * Returns null when the snapshot has no payload for this report/client.
 */
export async function loadSnapshotPayload(snapshot, reportId, clientKey) {
  if (!snapshot?.manifest) return null;
  const key = snapshotPartKey(reportId, clientKey);
  const chunkCount = Number(snapshot.manifest.parts?.[key]) || 0;
  if (!chunkCount) return null;
  const cacheKey = `${snapshot.tenantId}|${snapshot.ym}|${snapshot.manifest.id}|${key}`;
  if (payloadCache.has(cacheKey)) return payloadCache.get(cacheKey);
  const promise = (async () => {
    const parts = await Promise.all(
      Array.from({ length: chunkCount }, (_, i) =>
        getDoc(partRef(snapshot.tenantId, snapshot.ym, snapshot.manifest.id, `${key}__${i}`)),
      ),
    );
    if (parts.some((p) => !p.exists())) throw new Error('Frozen report snapshot is incomplete.');
    return reviveReport(parts.map((p) => p.data().data).join(''));
  })();
  payloadCache.set(cacheKey, promise);
  promise.catch(() => payloadCache.delete(cacheKey));
  return promise;
}

/**
 * Whether a frozen snapshot predates the month's current billing state: the
 * period was reopened (not finalized now) or re-finalized with a new billing
 * snapshot after the freeze.
 */
export function isSnapshotOutdated(snapshot, billingPeriod) {
  if (!snapshot?.manifest) return false;
  if (billingPeriod?.status !== 'finalized') return true;
  const frozenAgainst = snapshot.manifest.billingSnapshotVersion || null;
  return Boolean(frozenAgainst && billingPeriod.currentSnapshotVersion && frozenAgainst !== billingPeriod.currentSnapshotVersion);
}

function groupPatientsByClient(patients, clientSites) {
  const siteToClient = new Map((clientSites || []).map((s) => [s.id, String(s.clientId || '')]));
  const map = new Map();
  (patients || []).forEach((p) => {
    const clientId = siteToClient.get(p.clientSiteId) || UNASSIGNED_CLIENT_KEY;
    if (!map.has(clientId)) map.set(clientId, []);
    map.get(clientId).push(p);
  });
  return map;
}

function clientLabel(clientId, clients) {
  if (clientId === UNASSIGNED_CLIENT_KEY) return 'Unassigned (no client)';
  const client = (clients || []).find((c) => c.id === clientId);
  return client?.clientName || client?.clientShortName || clientId;
}

function mergeRpmInputs(list) {
  const programsByPatientId = new Map();
  const holdAudits = [];
  list.forEach((inputs) => {
    inputs.programsByPatientId.forEach((v, k) => programsByPatientId.set(k, v));
    holdAudits.push(...inputs.holdAudits);
  });
  return { programsByPatientId, holdAudits };
}

async function writePayload(tenantId, ym, versionId, key, payload) {
  const json = serializeReport(payload);
  const chunks = [];
  for (let i = 0; i < json.length; i += CHUNK_CHARS) chunks.push(json.slice(i, i + CHUNK_CHARS));
  if (!chunks.length) chunks.push('null');
  for (let i = 0; i < chunks.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await setDoc(partRef(tenantId, ym, versionId, `${key}__${i}`), { key, index: i, data: chunks[i] });
  }
  return chunks.length;
}

/**
 * Freeze every listed report for one month: per client (by the patient's client
 * at freeze time) plus an all-clients version. Uses all tenant patients (no
 * scope, status, or billing narrowing) so the snapshot is the complete picture.
 *
 * @param {{ ym: string, patients: object[], clients: object[], clientSites: object[],
 *   userNameById: Map, reportIds: string[], billingPeriod: object, actor: { uid: string, name: string },
 *   onProgress?: (p: { done: number, total: number, current: string }) => void }} args
 */
export async function freezeReportSnapshot({
  ym,
  patients,
  clients,
  clientSites,
  userNameById,
  reportIds,
  billingPeriod,
  actor,
  onProgress,
}) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant.');
  if (!/^\d{4}-\d{2}$/.test(ym || '')) throw new Error('Choose a month to freeze.');
  if (billingPeriod?.status !== 'finalized') throw new Error(`Billing for ${ym} must be finalized before freezing reports.`);
  if (!reportIds?.length) throw new Error('No reports to freeze.');

  const period = buildPeriod('custom', ym, ym);
  const versionId = `v${Date.now()}`;
  const byClient = groupPatientsByClient(patients, clientSites);
  const groups = [...byClient.entries()].map(([clientKey, list]) => ({
    clientKey,
    name: clientLabel(clientKey, clients),
    patients: list,
  }));
  const total = (groups.length + 1) * reportIds.length;
  let done = 0;

  await setDoc(versionRef(tenantId, ym, versionId), {
    ym,
    status: 'writing',
    schemaVersion: SNAPSHOT_SCHEMA_VERSION,
    reportIds,
    billingSnapshotVersion: billingPeriod.currentSnapshotVersion || null,
    frozenByUid: actor?.uid || null,
    frozenByName: actor?.name || null,
    createdAt: serverTimestamp(),
  });

  const parts = {};
  const clientsMeta = [];
  const rpmInputsList = [];

  const buildGroup = async (clientKey, name, groupPatients, ctxOverrides = {}) => {
    const ctx = createReportBuildContext({
      tenantId,
      patients: groupPatients,
      clients,
      clientSites,
      period,
      userNameById,
    });
    Object.assign(ctx, ctxOverrides);
    for (const reportId of reportIds) {
      onProgress?.({ done, total, current: `${name} · ${reportId}` });
      // eslint-disable-next-line no-await-in-loop
      const built = await buildClientReport(reportId, ctx);
      if (built) {
        const key = snapshotPartKey(reportId, clientKey);
        // eslint-disable-next-line no-await-in-loop
        parts[key] = await writePayload(tenantId, ym, versionId, key, {
          ...built,
          patientCount: groupPatients.length,
        });
      }
      done += 1;
    }
    clientsMeta.push({ key: clientKey, name, patientCount: groupPatients.length });
    return ctx;
  };

  for (const g of groups) {
    // eslint-disable-next-line no-await-in-loop
    const ctx = await buildGroup(g.clientKey, g.name, g.patients);
    // eslint-disable-next-line no-await-in-loop
    rpmInputsList.push(await ctx.getRpmInputs());
  }
  const allInputs = mergeRpmInputs(rpmInputsList);
  await buildGroup(ALL_CLIENTS_KEY, 'All clients', patients || [], {
    getRpmInputs: () => Promise.resolve(allInputs),
  });

  await updateDoc(versionRef(tenantId, ym, versionId), {
    status: 'ready',
    parts,
    clients: clientsMeta,
    patientCount: (patients || []).length,
    readyAt: serverTimestamp(),
  });

  await setDoc(
    snapshotRef(tenantId, ym),
    {
      ym,
      currentVersion: versionId,
      frozenAt: serverTimestamp(),
      frozenByUid: actor?.uid || null,
      frozenByName: actor?.name || null,
      billingSnapshotVersion: billingPeriod.currentSnapshotVersion || null,
      versionCount: increment(1),
    },
    { merge: true },
  );

  await logAuditEvent({
    category: 'reports',
    action: 'report_snapshot_frozen',
    entity: 'reportSnapshot',
    entityId: `${ym}/${versionId}`,
    details: { ym, versionId, reportIds, clientCount: groups.length, patientCount: (patients || []).length },
    tenantId,
  });

  onProgress?.({ done: total, total, current: '' });
  return { versionId, parts, clients: clientsMeta };
}
